// ISO 3166-1 alpha-2 country codes. Display names are derived at module load via
// Intl.DisplayNames so we do not hand-maintain ~250 country names (and stay in sync with the
// browser's CLDR data). Anything that cannot be resolved falls back to the raw code.

const ALPHA2_CODES: readonly string[] = [
  'AD', 'AE', 'AF', 'AG', 'AI', 'AL', 'AM', 'AO', 'AQ', 'AR', 'AS', 'AT', 'AU', 'AW', 'AX',
  'AZ', 'BA', 'BB', 'BD', 'BE', 'BF', 'BG', 'BH', 'BI', 'BJ', 'BL', 'BM', 'BN', 'BO', 'BQ',
  'BR', 'BS', 'BT', 'BV', 'BW', 'BY', 'BZ', 'CA', 'CC', 'CD', 'CF', 'CG', 'CH', 'CI', 'CK',
  'CL', 'CM', 'CN', 'CO', 'CR', 'CU', 'CV', 'CW', 'CX', 'CY', 'CZ', 'DE', 'DJ', 'DK', 'DM',
  'DO', 'DZ', 'EC', 'EE', 'EG', 'EH', 'ER', 'ES', 'ET', 'FI', 'FJ', 'FK', 'FM', 'FO', 'FR',
  'GA', 'GB', 'GD', 'GE', 'GF', 'GG', 'GH', 'GI', 'GL', 'GM', 'GN', 'GP', 'GQ', 'GR', 'GS',
  'GT', 'GU', 'GW', 'GY', 'HK', 'HM', 'HN', 'HR', 'HT', 'HU', 'ID', 'IE', 'IL', 'IM', 'IN',
  'IO', 'IQ', 'IR', 'IS', 'IT', 'JE', 'JM', 'JO', 'JP', 'KE', 'KG', 'KH', 'KI', 'KM', 'KN',
  'KP', 'KR', 'KW', 'KY', 'KZ', 'LA', 'LB', 'LC', 'LI', 'LK', 'LR', 'LS', 'LT', 'LU', 'LV',
  'LY', 'MA', 'MC', 'MD', 'ME', 'MF', 'MG', 'MH', 'MK', 'ML', 'MM', 'MN', 'MO', 'MP', 'MQ',
  'MR', 'MS', 'MT', 'MU', 'MV', 'MW', 'MX', 'MY', 'MZ', 'NA', 'NC', 'NE', 'NF', 'NG', 'NI',
  'NL', 'NO', 'NP', 'NR', 'NU', 'NZ', 'OM', 'PA', 'PE', 'PF', 'PG', 'PH', 'PK', 'PL', 'PM',
  'PN', 'PR', 'PS', 'PT', 'PW', 'PY', 'QA', 'RE', 'RO', 'RS', 'RU', 'RW', 'SA', 'SB', 'SC',
  'SD', 'SE', 'SG', 'SH', 'SI', 'SJ', 'SK', 'SL', 'SM', 'SN', 'SO', 'SR', 'SS', 'ST', 'SV',
  'SX', 'SY', 'SZ', 'TC', 'TD', 'TF', 'TG', 'TH', 'TJ', 'TK', 'TL', 'TM', 'TN', 'TO', 'TR',
  'TT', 'TV', 'TW', 'TZ', 'UA', 'UG', 'UM', 'US', 'UY', 'UZ', 'VA', 'VC', 'VE', 'VG', 'VI',
  'VN', 'VU', 'WF', 'WS', 'YE', 'YT', 'ZA', 'ZM', 'ZW',
];

function makeResolver(): (code: string) => string {
  try {
    const fmt = new Intl.DisplayNames(['en'], { type: 'region' });
    return (code: string) => {
      try {
        return fmt.of(code) ?? code;
      } catch {
        return code;
      }
    };
  } catch {
    return (code: string) => code;
  }
}

const resolve = makeResolver();

export type Country = { code: string; name: string };

export const COUNTRIES: Country[] = ALPHA2_CODES.map((code) => ({ code, name: resolve(code) })).sort(
  (a, b) => a.name.localeCompare(b.name),
);

/**
 * Safe ISO code -> display name. Never throws: null/blank/invalid codes return the raw
 * input (or an empty string), so a single bad value can never crash a render.
 */
export function countryLabel(code: string | null | undefined): string {
  if (code == null) return '';
  const trimmed = code.trim();
  if (trimmed === '') return '';
  return resolve(trimmed);
}

/**
 * Drop null/blank entries (locations created without a country yield a null code) and trim
 * what remains, so callers never render an empty chip or "()". Country is optional.
 */
export function presentableCountryCodes(
  codes: ReadonlyArray<string | null | undefined> | null | undefined,
): string[] {
  if (codes == null) return [];
  return codes
    .filter((c): c is string => c != null && c.trim() !== '')
    .map((c) => c.trim());
}
